/**
 * TipTap rich text editor for the newsletter body and its prayer requests, plus
 * the read-only renderers for both.
 *
 * The imported bodies use <br /> for line breaks, which StarterKit's hardBreak
 * handles, so imported text round-trips through the editor without being mangled.
 */
import { useEffect, useId, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extensions'
import TextField from '@mui/material/TextField'
import { Icon } from './Icon'
import { Field, Modal } from './ui'

/**
 * Plain text -> HTML, for values written before a field became rich text.
 * Mirrors textToHtml in worker/util.js so a legacy prayer request opens in the
 * editor looking exactly as it read on the page.
 */
export const textToHtml = (text) => {
  const s = String(text ?? '').trim()
  if (!s) return ''
  const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return s
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((block) => `<p>${esc(block).replace(/\n/g, '<br />')}</p>`)
    .join('')
}

/** True when the editor holds nothing but empty paragraphs. */
export const isBlankHtml = (html) => !String(html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim()

/* The list buttons used to be a bare "•" and "1.", which read as punctuation
   rather than as controls, so the lists went unnoticed. */
const ToolIcon = ({ children }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" fill="currentColor">
    {children}
  </svg>
)

const BulletListIcon = () => (
  <ToolIcon>
    <circle cx="2.2" cy="4" r="1.5" />
    <circle cx="2.2" cy="8" r="1.5" />
    <circle cx="2.2" cy="12" r="1.5" />
    <rect x="5.6" y="3.2" width="9.4" height="1.6" rx="0.8" />
    <rect x="5.6" y="7.2" width="9.4" height="1.6" rx="0.8" />
    <rect x="5.6" y="11.2" width="9.4" height="1.6" rx="0.8" />
  </ToolIcon>
)

const OrderedListIcon = () => (
  <ToolIcon>
    <text x="0" y="5.4" fontSize="5.2" fontFamily="inherit" fontWeight="700">
      1
    </text>
    <text x="0" y="9.7" fontSize="5.2" fontFamily="inherit" fontWeight="700">
      2
    </text>
    <text x="0" y="14" fontSize="5.2" fontFamily="inherit" fontWeight="700">
      3
    </text>
    <rect x="5.6" y="3.2" width="9.4" height="1.6" rx="0.8" />
    <rect x="5.6" y="7.2" width="9.4" height="1.6" rx="0.8" />
    <rect x="5.6" y="11.2" width="9.4" height="1.6" rx="0.8" />
  </ToolIcon>
)

const Btn = ({ active, onClick, title, children, disabled }) => (
  <button
    type="button"
    className={`editor-btn${active ? ' active' : ''}`}
    onClick={onClick}
    title={title}
    aria-label={title}
    aria-pressed={!!active}
    disabled={disabled}
  >
    {children}
  </button>
)

export function RichTextEditor({ value, onChange, placeholder = 'Write the update…', small, footer }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer' } },
        // Tiptap 3 put these two in StarterKit. Underline has no toolbar button, so
        // it would only be a hidden Ctrl+U; trailingNode would write an empty <p>
        // after every divider or quote that ends a saved body.
        underline: false,
        trailingNode: false,
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    // Tiptap 3 stopped re-rendering on every transaction. The toolbar's active and
    // disabled states and the word count are all read during render, so they need it.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  })

  // Adopt externally-set content (an AI rewrite being accepted, or a record loading).
  useEffect(() => {
    if (!editor) return
    const incoming = value || ''
    if (incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming, { emitUpdate: false })
    }
  }, [value, editor])

  // The address being edited while the link dialog is open; null while it is shut.
  const [linkDraft, setLinkDraft] = useState(null)
  const linkFieldId = useId()

  /* An empty address takes the link off, as it did when this was window.prompt — the
     browser's own box, which could not be styled, read out its own words, and on some
     phones was not offered at all. The editor keeps its selection while the dialog has
     focus, so the mark lands on the text that was selected when the button was pressed. */
  const applyLink = (href) => {
    setLinkDraft(null)
    const chain = editor.chain().focus().extendMarkRange('link')
    if (href) chain.setLink({ href }).run()
    else chain.unsetLink().run()
  }

  if (!editor) return <div className="skeleton" style={{ height: small ? 130 : 260 }} />

  const words = editor.state.doc.textContent.split(/\s+/).filter(Boolean).length
  const chars = editor.state.doc.textContent.length

  return (
    <div className="editor">
      <div className="editor-toolbar">
        <Btn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
          <strong>B</strong>
        </Btn>
        <Btn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
          <em>I</em>
        </Btn>
        <Btn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
          <s>S</s>
        </Btn>
        <span className="editor-sep" />
        {[2, 3, 4].map((level) => (
          <Btn
            key={level}
            active={editor.isActive('heading', { level })}
            onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
            title={`Heading ${level}`}
          >
            H{level - 1}
          </Btn>
        ))}
        <Btn active={editor.isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()} title="Paragraph">
          ¶
        </Btn>
        <span className="editor-sep" />
        <Btn
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet list (or start a line with “- ”)"
        >
          <BulletListIcon />
        </Btn>
        <Btn
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered list (or start a line with “1. ”)"
        >
          <OrderedListIcon />
        </Btn>
        {/* Nesting: Tab/Shift+Tab do this too, but only once you know to try it. */}
        <Btn
          onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
          disabled={!editor.can().sinkListItem('listItem')}
          title="Indent list item"
        >
          ⇥
        </Btn>
        <Btn
          onClick={() => editor.chain().focus().liftListItem('listItem').run()}
          disabled={!editor.can().liftListItem('listItem')}
          title="Outdent list item"
        >
          ⇤
        </Btn>
        <Btn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote">
          ❝
        </Btn>
        <span className="editor-sep" />
        <Btn active={editor.isActive('link')} onClick={() => setLinkDraft(editor.getAttributes('link').href || '')} title="Link">
          <Icon name="link" size={15} />
        </Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider">
          —
        </Btn>
        <span className="editor-sep" />
        <Btn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting">
          ⌫
        </Btn>
        <span className="spacer" />
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
          ↶
        </Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
          ↷
        </Btn>
      </div>
      <EditorContent className={`editor-content${small ? ' editor-content-sm' : ''}`} editor={editor} />
      <Modal
        open={linkDraft !== null}
        onClose={() => setLinkDraft(null)}
        title={editor.isActive('link') ? 'Edit Link' : 'Add Link'}
        size="sm"
        footer={
          <>
            <button type="submit" form={linkFieldId + '-form'} className="btn btn-primary" disabled={!linkDraft?.trim()}>
              Save link
            </button>
            {editor.isActive('link') && (
              <button type="button" className="btn" onClick={() => applyLink('')}>
                Remove link
              </button>
            )}
            <button type="button" className="btn" onClick={() => setLinkDraft(null)}>
              Cancel
            </button>
          </>
        }
      >
        {/* A form so that Enter in the box saves, the way it did in the prompt. */}
        <form
          id={linkFieldId + '-form'}
          onSubmit={(e) => {
            e.preventDefault()
            if (linkDraft?.trim()) applyLink(linkDraft.trim())
          }}
        >
          <Field label="Address" htmlFor={linkFieldId}>
            <TextField
              id={linkFieldId}
              fullWidth
              autoFocus
              value={linkDraft ?? ''}
              onChange={(e) => setLinkDraft(e.target.value)}
              placeholder="https://"
              slotProps={{ htmlInput: { inputMode: 'url', autoComplete: 'url', spellCheck: false } }}
            />
          </Field>
        </form>
      </Modal>
      <div className="editor-foot">
        <span className="nums">{words.toLocaleString()} words</span>
        <span aria-hidden="true">·</span>
        <span className="nums">{chars.toLocaleString()} characters</span>
        {footer && (
          <>
            <span className="spacer" />
            {footer}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Read-only render of stored newsletter HTML.
 *
 * The server sanitises on write (see worker/util.js sanitizeHtml), and the
 * imported corpus only contains <br>, so this is display of already-cleaned
 * content rather than a trust boundary.
 */
export const RichTextView = ({ html, serif, className = '' }) => {
  if (!html) return <span className="subtle">No text</span>
  return <div className={`prose${serif ? ' prose-serif' : ''} ${className}`} dangerouslySetInnerHTML={{ __html: html }} />
}

/**
 * Prayer requests, which are rich text on records saved since that field became
 * an editor and plain text on everything imported before it. Records written
 * through the API with only the legacy field also land here as plain text.
 */
export const PrayerText = ({ html, text, className = '' }) =>
  html && !isBlankHtml(html) ? (
    <RichTextView html={html} className={className} />
  ) : (
    <TextBlock text={text} className={className} />
  )

/** Plain-text block that keeps the source line breaks (prayer requests). */
export const TextBlock = ({ text, className = '' }) => {
  if (!text || !String(text).trim()) return <span className="subtle">—</span>
  return (
    <div className={className} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
      {String(text).replace(/<\s*br\s*\/?\s*>/gi, '\n').replace(/<[^>]+>/g, '')}
    </div>
  )
}
